import Handlebars from "handlebars";
import html2pdf from "html2pdf.js";

/**
 * Load credit note template based on current language
 */
export const loadCreditNoteTemplate = async () => {
  const currentLang = localStorage.getItem('i18nextLng') || 'en';
  const lang = currentLang.split('-')[0];
  const templatePath = `/creditnotetemplates/creditnote-${lang}.html`;
  
  try {
    const response = await fetch(templatePath);
    if (!response.ok) throw new Error('Template not found');
    return await response.text();
  } catch (error) {
    console.error("Error loading template:", error);
    const fallbackResponse = await fetch('/creditnotetemplates/creditnote-en.html');
    return await fallbackResponse.text();
  }
};

/**
 * Render credit note template with data
 * @param {Object} creditNote - The credit note data
 * @param {Object} storeInfo - Company/store information
 * @param {Object} t - Translation function
 */
export const renderCreditNoteTemplate = async (creditNote, storeInfo, t) => {
  if (!creditNote) return null;

  const template = await loadCreditNoteTemplate();
  const compiledTemplate = Handlebars.compile(template);

  // Calculate totals from line items
  const lineItems = creditNote.LineItems || [];
  let subTotal = 0;
  let vatAmount = 0;
  const vatByRate = {};

  const formattedLineItems = lineItems.map(item => {
    const qty = parseFloat(item.Quantity || 0);
    const unitPrice = parseFloat(item.UnitPrice || 0);
    const totalExcl = qty * unitPrice;
    const vatPercentage = parseFloat(item.VAT || item.VATPercentage || 0);
    const itemVat = (totalExcl * vatPercentage) / 100;
    const totalIncl = totalExcl + itemVat;

    subTotal += totalExcl;
    vatAmount += itemVat;

    // Group by VAT rate
    if (!vatByRate[vatPercentage]) {
      vatByRate[vatPercentage] = 0;
    }
    vatByRate[vatPercentage] += itemVat;

    return {
      UpdatedProductName: item.Product?.Name || item.ProductName || item.Description || `Product #${item.ProductId}`,
      Quantity: qty,
      UnitPrice: unitPrice.toFixed(2),
      VATPercentage: vatPercentage,
      TotalExcl: totalExcl.toFixed(2),
      VATAmount: itemVat.toFixed(2),
      TotalIncl: totalIncl.toFixed(2)
    };
  });

  const totalAmount = subTotal + vatAmount;

  // Convert vatByRate to array for template
  const vatBreakdown = Object.entries(vatByRate).map(([rate, amount]) => ({
    rate: rate,
    amount: amount.toFixed(2)
  }));

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const data = {
    // Company/Store Info
    CompanyName: storeInfo?.name || "",
    CompanyVAT: storeInfo?.vat || "",
    CompanyStreetAddress: storeInfo?.address || "",
    CompanyCityCountry: [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' ') || "",
    CompanyIBAN: storeInfo?.iban || "",
    
    // Customer Info - would need to be fetched from invoice if needed
    CustomerName: "Customer Name", // TODO: Fetch from invoice
    CustomerVAT: "",
    CustomerStreetAddress: "",
    CustomerCityCountry: "",
    
    // Credit Note Info
    CreditNoteNumber: creditNote.CreditNoteNumber || "",
    CreditNoteDate: formatDate(creditNote.CreditNoteDate),
    CreditNoteReason: creditNote.Reason || "",
    CreditType: "Full", // or "Partial" based on LineItems
    TotalQuantity: lineItems.length,
    
    // Original Invoice Reference
    OriginalInvoiceNumber: `Invoice #${creditNote.InvoiceId}`,
    InvoiceDate: formatDate(creditNote.CreatedDate),
    OriginalInvoiceAmount: totalAmount.toFixed(2),
    InvoiceTotalVATAmount: vatAmount.toFixed(2),
    
    // Line Items
    LineItems: formattedLineItems,
    
    // Totals
    SubTotal: subTotal.toFixed(2),
    VATAmount: vatAmount.toFixed(2),
    TotalAmount: totalAmount.toFixed(2),
    VATBreakdown: vatBreakdown,
    
    // Translations
    T_CREDIT_NOTE: t ? t('credit_note_title', 'CREDIT NOTE') : 'CREDIT NOTE',
    T_CREDIT_TO: t ? t('credit_to', 'Credit To') : 'Credit To',
    T_BILLED_TO: t ? t('billed_to', 'Billed to') : 'Billed to',
    T_VAT: t ? t('vat', 'VAT') : 'VAT',
    T_ADDRESS: t ? t('address', 'Address') : 'Address',
    T_IBAN: t ? t('iban', 'IBAN') : 'IBAN',
    T_CREDIT_NOTE_DATE: t ? t('credit_note_date', 'Credit Note Date') : 'Credit Note Date',
    T_ORIGINAL_INVOICE: t ? t('original_invoice', 'Original Invoice') : 'Original Invoice',
    T_INVOICE_DATE: t ? t('invoice_date', 'Invoice Date') : 'Invoice Date',
    T_REASON_FOR_CREDIT_NOTE: t ? t('reason_for_credit_note', 'Reason for Credit Note') : 'Reason for Credit Note',
    T_DESCRIPTION: t ? t('description', 'Description') : 'Description',
    T_QTY: t ? t('qty', 'Qty') : 'Qty',
    T_UNIT_PRICE: t ? t('unit_price', 'Unit Price') : 'Unit Price',
    T_NET_TOTAL: t ? t('net_total', 'Net Total') : 'Net Total',
    T_VAT_AMOUNT: t ? t('vat_amount', 'VAT Amount') : 'VAT Amount',
    T_SUB_TOTAL: t ? t('sub_total', 'Sub Total') : 'Sub Total',
    T_SUBTOTAL_EXCL_VAT: t ? t('subtotal_excl_vat', 'Subtotal (Excl. VAT)') : 'Subtotal (Excl. VAT)',
    T_TOTAL_CREDIT: t ? t('total_credit', 'TOTAL CREDIT') : 'TOTAL CREDIT',
    
    // Optional
    CustomText: "",
    GeneratedDate: new Date().toLocaleString(),
    PageNumber: 1,
    TotalPages: 1
  };

  return compiledTemplate(data);
};

/**
 * Generate PDF from HTML
 * @param {string} html - The HTML content
 * @param {string} filename - The PDF filename
 */
export const generateCreditNotePDF = async (html, filename) => {
  const opt = {
    margin: 0,
    filename: filename || 'credit-note.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2, 
      useCORS: true,
      logging: false,
      width: 794,
      windowWidth: 794,
      letterRendering: true
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait' 
    },
    pagebreak: { 
      mode: ['avoid-all', 'css', 'legacy'],
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: ['tr', 'td', 'img']
    }
  };
  
  const pdf = html2pdf().set(opt).from(html);
  const pdfBlob = await pdf.output('blob');
  
  return pdfBlob;
};

/**
 * Download credit note PDF
 * @param {Object} creditNote - The credit note data
 * @param {Object} storeInfo - Company/store information
 * @param {Object} t - Translation function
 */
export const downloadCreditNotePDF = async (creditNote, storeInfo, t) => {
  try {
    // Render template
    const html = await renderCreditNoteTemplate(creditNote, storeInfo, t);
    
    // Generate PDF
    const filename = `${creditNote.CreditNoteNumber || 'credit-note'}.pdf`;
    const pdfBlob = await generateCreditNotePDF(html, filename);
    
    // Download
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading credit note PDF:', error);
    throw error;
  }
};
